/**
 * Partner referral codes on marketing links (`https://www.landmate.app/?ref=CODE`).
 *
 * The Landmate web app credits a sign-up to a partner when its sign-up page is
 * opened as `/sign-up?ref=CODE` (web repository, docs/partners.md). A partner
 * may share a link to this site instead, so the site forwards a valid code to
 * the app's sign-up link. It keeps the code only in memory for the visit (no
 * cookie, no storage, nothing sent anywhere), and only a well-formed code:
 * 8 characters of Crockford base32, the app's own format. The app ignores
 * unknown or inactive codes, so the code is not a secret.
 */
export const REFERRAL_CODE = /^[0-9A-HJKMNP-TV-Z]{8}$/;

/** The normalized code (trimmed, upper case), or null if it is not a well-formed code. */
export function normalizeReferralCode(value: string | null | undefined): string | null {
  const code = (value ?? '').trim().toUpperCase();
  return REFERRAL_CODE.test(code) ? code : null;
}

/** The valid `ref` of a query string (`?ref=…`), or null. A repeated `ref` uses the first. */
export function referralFromSearch(search: string): string | null {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  return normalizeReferralCode(params.get('ref'));
}

/**
 * The code to remember after visiting a page with this query: a valid `ref`
 * replaces the one remembered so far; anything else keeps it, so following an
 * internal link (which drops the query) does not lose it.
 */
export function nextReferral(current: string | null, search: string): string | null {
  return referralFromSearch(search) ?? current;
}

/** `url` with `ref=<code>` added to its query (no change without a code). */
export function withReferral(url: string, code: string | null): string {
  if (!code) return url;
  const parsed = new URL(url);
  parsed.searchParams.set('ref', code);
  return parsed.toString();
}
